import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  Send, RefreshCw, FileText, CheckCircle2, Clock, AlertTriangle, XCircle,
  MessageCircle, Eye, ExternalLink, BadgeCheck, PauseCircle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const brl = (v) => (Number(v) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fmtDT = (s) => {
  if (!s) return "—";
  const d = new Date(s.length <= 10 ? `${s}T00:00:00` : s);
  if (isNaN(d)) return s;
  return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};
const fmtD = (s) => {
  if (!s) return "—";
  const d = new Date(`${(s || "").slice(0, 10)}T00:00:00`);
  if (isNaN(d)) return s;
  return d.toLocaleDateString("pt-BR");
};
const mesLabel = (m) => {
  if (!m || !/^\d{4}-\d{2}$/.test(m)) return m || "—";
  const [y, mm] = m.split("-");
  const nomes = ["", "jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
  return `${nomes[parseInt(mm, 10)]}/${y}`;
};

// ---- status de boleto ----
const BOLETO_STATUS = {
  PAID: { label: "Pago", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
  RECEIVED: { label: "Pago", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
  CONFIRMED: { label: "Pago", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
  PENDING: { label: "Pendente", cls: "bg-amber-50 text-amber-700 border-amber-200", icon: Clock },
  OVERDUE: { label: "Vencido", cls: "bg-red-50 text-red-700 border-red-200", icon: AlertTriangle },
  REFUNDED: { label: "Estornado", cls: "bg-slate-100 text-slate-600 border-slate-200", icon: XCircle },
  DELETED: { label: "Excluído", cls: "bg-slate-100 text-slate-500 border-slate-200", icon: XCircle },
};
const boletoBadge = (st) => BOLETO_STATUS[(st || "").toUpperCase()] || { label: st || "—", cls: "bg-slate-100 text-slate-600 border-slate-200", icon: Clock };
const isPago = (st) => ["PAID", "RECEIVED", "CONFIRMED"].includes((st || "").toUpperCase());

// ---- status de envio (aceite) ----
const ENVIO_STATUS = {
  enviado: { label: "Aceito p/ envio", cls: "bg-sky-50 text-sky-700 border-sky-200", icon: Send },
  erro: { label: "Erro", cls: "bg-red-50 text-red-700 border-red-200", icon: XCircle },
  pausado: { label: "Pausado", cls: "bg-slate-100 text-slate-500 border-slate-200", icon: PauseCircle },
  desativado: { label: "Desativado", cls: "bg-slate-100 text-slate-500 border-slate-200", icon: PauseCircle },
  pendente: { label: "Pendente", cls: "bg-amber-50 text-amber-700 border-amber-200", icon: Clock },
};
const envioBadge = (st) => ENVIO_STATUS[(st || "").toLowerCase()] || { label: st || "—", cls: "bg-slate-100 text-slate-600 border-slate-200", icon: Clock };

// ---- status de entrega Meta ----
const ENTREGA_STATUS = {
  read: { label: "Lido", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: BadgeCheck },
  delivered: { label: "Entregue", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
  sent: { label: "Enviado", cls: "bg-sky-50 text-sky-700 border-sky-200", icon: Send },
  failed: { label: "Falhou", cls: "bg-red-50 text-red-700 border-red-200", icon: XCircle },
};
const TIPO_LABEL = { fatura: "Boleto/Fatura", lembrete: "Lembrete", estoque_minimo: "Alerta de estoque" };

const Badge = ({ cfg, testid }) => {
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border ${cfg.cls}`} data-testid={testid}>
      <Icon className="w-3.5 h-3.5" />{cfg.label}
    </span>
  );
};

const Kpi = ({ label, value, hint, tone = "slate", testid }) => (
  <Card className="border-slate-200" data-testid={testid}>
    <CardContent className="p-4">
      <div className="text-xs font-medium text-slate-500">{label}</div>
      <div className={`text-2xl font-bold mt-1 text-${tone}-700`}>{value}</div>
      {hint && <div className="text-[11px] text-slate-400 mt-0.5">{hint}</div>}
    </CardContent>
  </Card>
);

export default function PainelEnvios() {
  const [loading, setLoading] = useState(true);
  const [boletos, setBoletos] = useState([]);
  const [envios, setEnvios] = useState([]);
  const [busca, setBusca] = useState("");
  const [fStatusBoleto, setFStatusBoleto] = useState("todos");
  const [fTipoEnvio, setFTipoEnvio] = useState("todos");

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await axios.get(`${API}/painel-envios`);
      setBoletos(data.boletos || []);
      setEnvios(data.envios || []);
    } catch (e) {
      toast.error("Falha ao carregar o painel de envios.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const boletosF = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return boletos.filter((b) => {
      if (fStatusBoleto === "pagos" && !isPago(b.status)) return false;
      if (fStatusBoleto === "pendentes" && isPago(b.status)) return false;
      if (fStatusBoleto === "vencidos" && (b.status || "").toUpperCase() !== "OVERDUE") return false;
      if (q && !(`${b.residenteNome} ${b.mes}`.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [boletos, busca, fStatusBoleto]);

  const enviosF = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return envios.filter((n) => {
      if (fTipoEnvio !== "todos" && n.tipo !== fTipoEnvio) return false;
      if (q && !(`${n.residenteNome} ${n.familiarNome} ${n.mensagem}`.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [envios, busca, fTipoEnvio]);

  const kpis = useMemo(() => ({
    totalBoletos: boletos.length,
    pagos: boletos.filter((b) => isPago(b.status)).length,
    pendentes: boletos.filter((b) => !isPago(b.status) && (b.status || "").toUpperCase() !== "OVERDUE").length,
    vencidos: boletos.filter((b) => (b.status || "").toUpperCase() === "OVERDUE").length,
    enviados: envios.filter((n) => n.status === "enviado").length,
    erros: envios.filter((n) => n.status === "erro").length,
  }), [boletos, envios]);

  return (
    <div className="space-y-6" data-testid="painel-envios-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Send className="w-6 h-6 text-sky-600" /> Painel de Envios
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Boletos gerados, status de pagamento e histórico de envios por WhatsApp/E-mail.</p>
        </div>
        <Button variant="outline" onClick={() => load()} data-testid="btn-recarregar-envios">
          <RefreshCw className="w-4 h-4 mr-2" /> Atualizar
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Kpi label="Boletos gerados" value={kpis.totalBoletos} tone="slate" testid="kpi-total-boletos" />
        <Kpi label="Pagos" value={kpis.pagos} tone="emerald" testid="kpi-pagos" />
        <Kpi label="Pendentes" value={kpis.pendentes} tone="amber" testid="kpi-pendentes" />
        <Kpi label="Vencidos" value={kpis.vencidos} tone="red" testid="kpi-vencidos" />
        <Kpi label="Envios OK" value={kpis.enviados} tone="sky" testid="kpi-enviados" />
        <Kpi label="Envios c/ erro" value={kpis.erros} tone="red" testid="kpi-erros" />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Input
          placeholder="Buscar por residente, mês ou responsável…"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          className="max-w-sm"
          data-testid="input-busca-envios"
        />
      </div>

      <Tabs defaultValue="boletos">
        <TabsList data-testid="envios-tabs">
          <TabsTrigger value="boletos" data-testid="tab-boletos">
            <FileText className="w-4 h-4 mr-1.5" /> Boletos ({boletosF.length})
          </TabsTrigger>
          <TabsTrigger value="envios" data-testid="tab-envios">
            <MessageCircle className="w-4 h-4 mr-1.5" /> Envios ({enviosF.length})
          </TabsTrigger>
        </TabsList>

        {/* ---------- BOLETOS ---------- */}
        <TabsContent value="boletos" className="mt-4">
          <div className="mb-3 w-48">
            <Select value={fStatusBoleto} onValueChange={setFStatusBoleto}>
              <SelectTrigger data-testid="filtro-status-boleto"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos os status</SelectItem>
                <SelectItem value="pagos">Pagos</SelectItem>
                <SelectItem value="pendentes">Pendentes</SelectItem>
                <SelectItem value="vencidos">Vencidos</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Card className="border-slate-200">
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                  <tr>
                    <th className="text-left px-4 py-3">Residente</th>
                    <th className="text-left px-4 py-3">Mês</th>
                    <th className="text-right px-4 py-3">Valor</th>
                    <th className="text-left px-4 py-3">Vencimento</th>
                    <th className="text-left px-4 py-3">Gerado em</th>
                    <th className="text-left px-4 py-3">Status</th>
                    <th className="text-left px-4 py-3">Boleto</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loading && (
                    <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">Carregando…</td></tr>
                  )}
                  {!loading && boletosF.length === 0 && (
                    <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400" data-testid="boletos-vazio">Nenhum boleto encontrado.</td></tr>
                  )}
                  {boletosF.map((b) => {
                    const bb = boletoBadge(b.status);
                    return (
                      <tr key={b.id} className="hover:bg-slate-50" data-testid={`boleto-row-${b.id}`}>
                        <td className="px-4 py-3 font-medium text-slate-800">{b.residenteNome}</td>
                        <td className="px-4 py-3 text-slate-600">{mesLabel(b.mes)}</td>
                        <td className="px-4 py-3 text-right font-semibold text-slate-800">{brl(b.valor)}</td>
                        <td className="px-4 py-3 text-slate-600">{fmtD(b.vencimento)}</td>
                        <td className="px-4 py-3 text-slate-500">{fmtDT(b.criadoEm)}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5">
                            <Badge cfg={bb} testid={`boleto-status-${b.id}`} />
                            {b.sandbox && <span className="text-[10px] font-semibold text-amber-600" title="Ambiente de teste">SANDBOX</span>}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {(b.bankSlipUrl || b.invoiceUrl) ? (
                            <a href={b.bankSlipUrl || b.invoiceUrl} target="_blank" rel="noreferrer"
                               className="inline-flex items-center gap-1 text-sky-600 hover:underline text-xs font-medium"
                               data-testid={`ver-boleto-${b.id}`}>
                              <Eye className="w-3.5 h-3.5" /> Ver <ExternalLink className="w-3 h-3" />
                            </a>
                          ) : <span className="text-slate-300 text-xs">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ---------- ENVIOS ---------- */}
        <TabsContent value="envios" className="mt-4">
          <div className="mb-3 w-52">
            <Select value={fTipoEnvio} onValueChange={setFTipoEnvio}>
              <SelectTrigger data-testid="filtro-tipo-envio"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos os tipos</SelectItem>
                <SelectItem value="fatura">Boleto/Fatura</SelectItem>
                <SelectItem value="lembrete">Lembrete</SelectItem>
                <SelectItem value="estoque_minimo">Alerta de estoque</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Card className="border-slate-200">
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                  <tr>
                    <th className="text-left px-4 py-3">Tipo</th>
                    <th className="text-left px-4 py-3">Destinatário</th>
                    <th className="text-left px-4 py-3">Mensagem</th>
                    <th className="text-left px-4 py-3">Enviado</th>
                    <th className="text-left px-4 py-3">Status</th>
                    <th className="text-left px-4 py-3">Entrega (WhatsApp)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loading && (
                    <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">Carregando…</td></tr>
                  )}
                  {!loading && enviosF.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400" data-testid="envios-vazio">Nenhum envio registrado.</td></tr>
                  )}
                  {enviosF.map((n) => {
                    const eb = envioBadge(n.status);
                    const entrega = ENTREGA_STATUS[(n.waStatus || "").toLowerCase()];
                    return (
                      <tr key={n.id} className="hover:bg-slate-50 align-top" data-testid={`envio-row-${n.id}`}>
                        <td className="px-4 py-3">
                          <span className="text-xs font-medium text-slate-700">{TIPO_LABEL[n.tipo] || n.tipo}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-slate-800">{n.familiarNome || n.residenteNome || "—"}</div>
                          {n.residenteNome && n.familiarNome && <div className="text-[11px] text-slate-400">Res.: {n.residenteNome}</div>}
                          {n.familiarTelefone && <div className="text-[11px] text-slate-400">{n.familiarTelefone}</div>}
                        </td>
                        <td className="px-4 py-3 text-slate-600 max-w-xs">
                          <div className="line-clamp-2">{n.mensagem}</div>
                          {n.erro && <div className="text-[11px] text-red-500 mt-0.5 line-clamp-2" title={n.erro}>{n.erro}</div>}
                        </td>
                        <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{fmtDT(n.dataEnvio || n.dataCriacao)}</td>
                        <td className="px-4 py-3"><Badge cfg={eb} testid={`envio-status-${n.id}`} /></td>
                        <td className="px-4 py-3">
                          {entrega ? (
                            <div>
                              <Badge cfg={entrega} testid={`envio-entrega-${n.id}`} />
                              <div className="text-[10px] text-slate-400 mt-0.5">{fmtDT(n.waStatusEm)}</div>
                            </div>
                          ) : (
                            <span className="text-xs text-slate-300" data-testid={`envio-entrega-${n.id}`}>—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </CardContent>
          </Card>
          <p className="text-[11px] text-slate-400 mt-2">
            "Aceito p/ envio" = a Meta aceitou a mensagem. O status de <b>Entrega</b> (Enviado/Entregue/Lido) só aparece para envios novos após a Meta confirmar o recebimento no aparelho.
          </p>
        </TabsContent>
      </Tabs>
    </div>
  );
}
